import { Injectable } from '@angular/core';

// Font configuration for PDFMake
export interface FontDefinition {
  normal: string;
  bold: string;
  italics: string;
  bolditalics: string;
}

export interface PDFMakeVFS {
  [key: string]: string;
}

@Injectable({
  providedIn: 'root',
})
export class PdfFontLoaderService {
  private fontsLoaded = false;
  private customVFS: PDFMakeVFS | null = null;

  constructor() { }

  /**
   * Load and create custom VFS with Roboto fonts
   * @returns Promise<{vfs: PDFMakeVFS, fonts: {[key: string]: FontDefinition}}>
   */
  async loadRobotoFonts(): Promise<{ vfs: PDFMakeVFS; fonts: { [key: string]: FontDefinition } }> {
    if (this.fontsLoaded && this.customVFS) {
      return {
        vfs: this.customVFS,
        fonts: this.getRobotoFontDefinition(),
      };
    }

    try {
      // Load all font files in parallel
      const [regular, bold, italic, boldItalic] = await Promise.all([
        this.loadFontFile('/assets/fonts/Roboto-Regular.ttf'),
        this.loadFontFile('/assets/fonts/Roboto-Bold.ttf'),
        this.loadFontFile('/assets/fonts/Roboto-Italic.ttf'),
        this.loadFontFile('/assets/fonts/Roboto-BoldItalic.ttf'),
      ]);

      // Create VFS with base64 encoded fonts
      this.customVFS = {
        'Roboto-Regular.ttf': regular,
        'Roboto-Bold.ttf': bold,
        'Roboto-Italic.ttf': italic,
        'Roboto-BoldItalic.ttf': boldItalic,
      };

      this.fontsLoaded = true;

      return {
        vfs: this.customVFS,
        fonts: this.getRobotoFontDefinition(),
      };
    } catch (error) {
      console.error('Failed to load Roboto fonts:', error);
      throw new Error('Failed to load Roboto fonts: ' + (error as Error).message);
    }
  }

  /**
   * Get font definition for Roboto
   */
  private getRobotoFontDefinition(): { [key: string]: FontDefinition } {
    return {
      Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Bold.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-BoldItalic.ttf',
      },
    };
  }

  /**
   * Load a font file and convert to base64
   */
  private async loadFontFile(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch font from ${url}: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      // Convert to base64
      const base64 = this.arrayBufferToBase64(uint8Array);
      return base64;
    } catch (error) {
      console.error(`Error loading font from ${url}:`, error);
      throw error;
    }
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const bytes = buffer;
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    // Use window.btoa explicitly to prevent minification issues
    return window.btoa(binary);
  }

  /**
   * Check if fonts are already loaded
   */
  isLoaded(): boolean {
    return this.fontsLoaded;
  }

  /**
   * Get loaded VFS (returns null if not loaded)
   */
  getVFS(): PDFMakeVFS | null {
    return this.customVFS;
  }

  /**
   * Get font definitions (returns empty object if not loaded)
   */
  getFontDefinitions(): { [key: string]: FontDefinition } {
    return this.fontsLoaded ? this.getRobotoFontDefinition() : {};
  }
}